import React, { createContext, use } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";

import type { Operations } from "@workspace/operations";
import { useProfile } from "@workspace/profile";

import { venueStaffManagementApi } from "../api";
import { emptyStaffData } from "../emptyStaffData";
import type { Contract, Shift, StaffMember } from "../types";

export function useStaffingOverview({
	venueId,
	operations,
}: { venueId: string } & Operations) {
	const { tenant, userId } = useProfile();

	const getStaffing = useSuspenseQuery({
		...venueStaffManagementApi.getDashboardOptions(tenant, venueId, "staffing"),
		select: (data) => {
			return data.data.staff || emptyStaffData;
		},
	});

	const data = getStaffing.data;

	const [searchTerm, setSearchTerm] = React.useState("");

	const [contractStatusFilter, setContractStatusFilter] = React.useState("all");
	const [contractTypeFilter, setContractTypeFilter] = React.useState("all");
	const [isContractDialogOpen, setIsContractDialogOpen] = React.useState(false);
	const [isResetPinDialogOpen, setIsResetPinDialogOpen] = React.useState(false);

	// HA-283 — archive/restore share one confirmation dialog; the mode decides its copy and which
	// mutation fires, so an archived row can never be handed to the archive call.
	const [showArchived, setShowArchived] = React.useState(false);
	const [isArchiveDialogOpen, setIsArchiveDialogOpen] = React.useState(false);
	const [archiveMode, setArchiveMode] = React.useState<"archive" | "restore">(
		"archive",
	);

	const [isContractDetailsOpen, setIsContractDetailsOpen] =
		React.useState(false);
	const [selectedContract, setSelectedContract] =
		React.useState<Contract | null>(null);

	const [selectedStaffMember, setSelectedStaffMember] =
		React.useState<StaffMember | null>(null);
	const [isStaffFormOpen, setIsStaffFormOpen] = React.useState(false);
	const [staffFormMode, setStaffFormMode] = React.useState<"add" | "edit">(
		"add",
	);
	const [editingStaffMember, setEditingStaffMember] =
		React.useState<StaffMember | null>(null);

	const [isShiftFormOpen, closeForm] = React.useState(false);
	const [editingShift, setEditingShift] = React.useState<Shift | null>(null);
	const [shiftViewMode, setShiftViewMode] = React.useState<"calendar" | "list">(
		"calendar",
	);

	const fetch = getStaffing.refetch;

	const sync = fetch;

	function setIsShiftFormOpen(open: boolean) {
		closeForm(open);
		if (!open) {
			setEditingShift(null);
		}
	}

	// HA-283 AC1 — archived members leave the active staff list. The toggle keeps them reachable
	// so Restore (AC7) has a surface to live on; the dedicated Archived list view is its own story.
	const archivedCount = data.staffMembers.filter(
		(staff) => staff.isArchived === true,
	).length;

	const filteredStaff = data.staffMembers
		.filter((staff) =>
			showArchived ? staff.isArchived === true : staff.isArchived !== true,
		)
		.filter(
			(staff) =>
				staff.personalDetails.preferredName
					.toLowerCase()
					.includes(searchTerm.toLowerCase()) ||
				staff.role.label.toLowerCase().includes(searchTerm.toLowerCase()),
		);

	// Get all contracts and apply filters
	const getAllContracts = () => {
		return data.staffMembers.flatMap((staff) =>
			staff.contracts.map((contract) => ({
				...contract,
				staffName: staff.personalDetails.preferredName,
				staffImage: staff.personalDetails.image,
			})),
		);
	};

	const allContracts = getAllContracts();
	const filteredContracts = allContracts.filter((contract) => {
		if (
			contractStatusFilter !== "all" &&
			contract.status !== contractStatusFilter
		)
			return false;
		if (contractTypeFilter !== "all" && contract.type !== contractTypeFilter)
			return false;
		if (
			searchTerm &&
			!contract.staffName.toLowerCase().includes(searchTerm.toLowerCase()) &&
			!contract.position.toLowerCase().includes(searchTerm.toLowerCase())
		)
			return false;

		return true;
	});

	// Handler for opening the add staff form
	const handleAddStaff = () => {
		setStaffFormMode("add");
		setEditingStaffMember(null);
		setIsStaffFormOpen(true);
	};

	async function setShiftStatus(_shift: object) {
		// return await changeShiftStatus(venueId, shift.id, {
		// 	shiftId: shift.id,
		// 	checkInTime: new Date(),
		// 	newStatus: shift.status,
		// });
	}

	const current = React.useMemo(() => {
		const staff =
			getStaffing.data.staffMembers.find(
				(x) => x.registeredUserId === userId,
			) || null;

		if (!staff) {
			return null;
		}

		return {
			...staff,
			role: getStaffing.data.settings.staffRoles.find(
				(x) => x.key === staff.role.key,
			) || {
				id: "",
				key: "",
				label: "",
				permissions: [],
				level: -1,
			},
		};
	}, [getStaffing.data, userId]);

	return {
		venueId,
		operations,

		filteredStaff,
		fetch,
		sync,

		data,

		searchTerm,
		setSearchTerm,
		contractStatusFilter,
		setContractStatusFilter,
		contractTypeFilter,
		setContractTypeFilter,

		selectedStaffMember,
		setSelectedStaffMember,
		handleAddStaff,
		setIsStaffFormOpen,
		isStaffFormOpen,
		staffFormMode,
		setStaffFormMode,
		editingStaffMember,
		setEditingStaffMember,
		setShiftStatus,

		isResetPinDialogOpen,
		setIsResetPinDialogOpen,

		showArchived,
		setShowArchived,
		archivedCount,
		isArchiveDialogOpen,
		setIsArchiveDialogOpen,
		archiveMode,
		setArchiveMode,

		isContractDialogOpen,
		setIsContractDialogOpen,
		isContractDetailsOpen,
		setIsContractDetailsOpen,
		selectedContract,
		setSelectedContract,
		filteredContracts,
		allContracts,

		shifts: data.shifts,
		isShiftFormOpen,
		setIsShiftFormOpen,
		editingShift,
		setEditingShift,
		shiftViewMode,
		setShiftViewMode,

		current,
	};
}

export type StaffDashboardContext = ReturnType<typeof useStaffingOverview>;

export const StaffDashboardContext = createContext<
	StaffDashboardContext | undefined
>(undefined);

export const useStaffDashboardContext = () => {
	const context = use(StaffDashboardContext);
	if (!context) {
		throw new Error(
			"useStaffDashboardContext must be used within a StaffDashboardContextProvider",
		);
	}

	return context;
};
